import { ApiRoutes } from "@/enums/apiRoutes.enum";
import axios from "@/lib/axios";
import {
  LegalConsent,
  LegalConsentUpsertPayload,
  LegalDocumentDetail,
  LegalDocumentSummary,
  LegalDocumentUpsertPayload,
  LegalDocumentVersionPayload,
} from "@/types/data";

// Legal documents & consents ("Dokumenti i privole"). Public reads + admin
// catalog management. These endpoints are isolated behind their own BFF proxy
// (src/pages/api/legal/[...slug].ts) because they live on a different host
// (NEXT_PUBLIC_DEV_MINCH_API_URL) than the rest of the v2 API, while still
// requiring the STATS_API_TOKEN bearer (injected server-side).
// Contract: documentation/features/Endpoint Contract (2).md
export const legalService = {
  // Full text of one document — public endpoint, active documents only.
  // The contract documents this as /documents/:id, but the backend actually
  // resolves the path param by `code` (ids 404) — verified against dev.
  async getDocument(code: string): Promise<LegalDocumentDetail> {
    const response = await axios.get<LegalDocumentDetail>(
      `${ApiRoutes.LEGAL_DOCUMENTS}/${code}`,
    );
    return response.data;
  },

  // --- admin documents ---

  async getAdminDocuments(): Promise<LegalDocumentSummary[]> {
    // The admin list names the published version `currentVersion` (the public
    // list calls it `version`) and omits `shortDescription` — normalize so
    // the UI reads one shape.
    const response = await axios.get<{
      documents: (LegalDocumentSummary & { currentVersion?: string | null })[];
    }>(ApiRoutes.LEGAL_ADMIN_DOCUMENTS);
    return response.data.documents.map(({ currentVersion, ...doc }) => ({
      ...doc,
      version: doc.version ?? currentVersion ?? null,
    }));
  },

  async createDocument(payload: LegalDocumentUpsertPayload) {
    const response = await axios.post(ApiRoutes.LEGAL_ADMIN_DOCUMENTS, payload);
    return response.data;
  },

  async updateDocument(code: string, payload: LegalDocumentUpsertPayload) {
    const response = await axios.patch(
      `${ApiRoutes.LEGAL_ADMIN_DOCUMENTS}/${code}`,
      payload,
    );
    return response.data;
  },

  async publishDocumentVersion(
    code: string,
    payload: LegalDocumentVersionPayload,
  ) {
    const response = await axios.post(
      `${ApiRoutes.LEGAL_ADMIN_DOCUMENTS}/${code}/versions`,
      payload,
    );
    return response.data;
  },

  // --- admin consents ---

  async getAdminConsents(): Promise<LegalConsent[]> {
    const response = await axios.get<{ consents: LegalConsent[] }>(
      ApiRoutes.LEGAL_ADMIN_CONSENTS,
    );
    return response.data.consents;
  },

  async createConsent(payload: LegalConsentUpsertPayload) {
    const response = await axios.post(ApiRoutes.LEGAL_ADMIN_CONSENTS, payload);
    return response.data;
  },

  async updateConsent(code: string, payload: LegalConsentUpsertPayload) {
    const response = await axios.patch(
      `${ApiRoutes.LEGAL_ADMIN_CONSENTS}/${code}`,
      payload,
    );
    return response.data;
  },
};
